/* eslint formatjs/no-literal-string-in-jsx: "error" */
import { FormattedMessage, useIntl } from "react-intl"
import { PlanInfoBadgeContainer } from "../../../../containers/planinfo/PlanInfoBadgeContainer"
import React from "react"
import {
  getCompletedReimbursementsList,
  getPendingReimbursementsList,
  getReimbursementsRequiringActionList,
  shouldShowPairedFunds
} from "../ReimbursementListUtils"
import { Legend, ReimbursementsListSection, ReimbursementSummary, TableHeader } from "../SharedReimbursementElements"
import { useSelector } from "react-redux"
import { getCardState } from "#/redux/reducers/userInfo"
import { getSubsidies } from "#/redux/reducers/subsidies"
import CardStates from "../../../../../utils/CardStates"
import { ExpenseState } from "../../../../../utils/ExpenseState"
import { Badge, Box, Link, Typography, useTheme, Stack, Tile } from "@carrotfertility/carotene-core"
import { DbgPaths } from "#/utils/Paths"
import icnTransactionSrc from "./assets/images/icn-transaction.png"
import icnTransaction2xSrc from "./assets/images/icn-transaction-2x.png"
import { TextDirection } from "#/types/Locale"
import { useTextDirection } from "../../../../../services/locale/hooks/useTextDirection"
import { Subsidy } from "#/lib/carrot-api/types/Subsidy"
import { getCompanyCurrencyCode } from "#/redux/reducers/companyInfo"

const EmptyReimbursementsList = () => {
  const isCardEligible = useSelector(getCardState) !== CardStates.INELIGIBLE
  return (
    <div>
      <Box paddingTop={(theme) => theme.tokens.spacing.md} />
      <table style={{ inlineSize: "100%", borderCollapse: "collapse", borderSpacing: 0 }}>
        <thead>
          <tr>
            <TableHeader />
          </tr>
        </thead>
        <tbody>
          {/* This color should be specified using a carotene-core palette color, but that kind of requires refactoring
              this whole table into a carotene-core Table which is going to be nontrivial. See LG-1524. */}
          <tr style={{ borderBlockStart: "0.125rem solid #b9b9b9" }}>
            <td colSpan={3}>
              <Box sx={{ inlineSize: "100%", display: "flex", justifyContent: "center" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", maxInlineSize: "360px" }}>
                  <Box
                    padding={(theme) => theme.tokens.spacing.md}
                    borderRadius={(theme) => theme.tokens.borderRadius.round}
                    marginTop={(theme) => theme.tokens.spacing.xxl}
                    height="84px"
                    width="84px"
                    sx={(theme) => ({ backgroundColor: theme.palette.secondary.light })}
                  >
                    <img
                      src={icnTransactionSrc}
                      srcSet={`${icnTransaction2xSrc} 2x`}
                      alt=""
                      height="50px"
                      width="50px"
                    />
                  </Box>
                  <Typography
                    variant="body1"
                    color="tertiary"
                    align="center"
                    marginTop={(theme) => theme.tokens.spacing.lg}
                  >
                    {isCardEligible ? (
                      <FormattedMessage defaultMessage="Your Carrot Card® transactions and reimbursement requests will appear here." />
                    ) : (
                      <FormattedMessage defaultMessage="Your reimbursement requests will appear here." />
                    )}
                  </Typography>
                </Box>
              </Box>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

const BalanceTile = ({ title, value }: { title: React.ReactNode; value: React.ReactNode }) => (
  <Tile variant="primary" color="primary" component="dl" sx={{ flex: 1, minInlineSize: "12.5rem", margin: 0 }}>
    <Typography component="dt" variant="body1" sx={(theme) => ({ fontWeight: theme.typography.fontWeightBold })}>
      {title}
    </Typography>
    <Typography
      component="dd"
      variant="h4"
      sx={(theme) => ({ marginInlineStart: 0, marginBlockStart: theme.tokens.spacing.sm })}
    >
      {value}
    </Typography>
  </Tile>
)

const BalanceSection = ({
  title,
  description,
  children
}: React.PropsWithChildren<{ title: React.ReactNode; description: React.ReactNode }>) => (
  <Box sx={(theme) => ({ paddingBlockEnd: theme.tokens.spacing.xxl })}>
    <Typography variant="h2" sx={(theme) => ({ paddingBlockEnd: theme.tokens.spacing.xs })}>
      {title}
    </Typography>
    <Typography
      variant="body1"
      sx={(theme) => ({ color: theme.palette.text.secondary, paddingBlockEnd: theme.tokens.spacing.md })}
    >
      {description}
    </Typography>
    <Stack direction="row" sx={(theme) => ({ flexWrap: "wrap", gap: theme.tokens.spacing.md })}>
      {children}
    </Stack>
  </Box>
)

const dbgLink = (linkContent: React.ReactNode) => (
  <Link href={DbgPaths.GET_STARTED} color="inherit">
    {linkContent}
  </Link>
)

const CREDITS_PER_CARROT_CYCLE = 12

// Credits are sold in whole Carrot Cycles; skip the sentence if a plan isn't.
const CarrotCycles = ({ creditLifetimeMax }: { creditLifetimeMax: number }) => {
  const cycles = creditLifetimeMax / CREDITS_PER_CARROT_CYCLE
  if (!Number.isInteger(cycles) || cycles < 1) return null
  return (
    <>
      <FormattedMessage
        defaultMessage="{cycles, plural, one {Your benefit includes # Carrot Cycle ({credits} credits).} other {Your benefit includes # Carrot Cycles ({credits} credits each).}}"
        values={{ cycles, credits: CREDITS_PER_CARROT_CYCLE }}
      />{" "}
    </>
  )
}

// Credit members: credits first, then the paired fund's dollars when there are any to use.
const CreditBalances = ({ creditSubsidy, fundSubsidy }: { creditSubsidy: Subsidy; fundSubsidy: Subsidy | null }) => {
  const intl = useIntl()
  const currencyCode = useSelector(getCompanyCurrencyCode)
  const formatCredits = (count: number) =>
    intl.formatMessage({ defaultMessage: "{count, plural, one {# credit} other {# credits}}" }, { count })
  const formatFunds = (amount: number) =>
    intl.formatNumber(Math.max(amount / 100, 0), {
      style: "currency",
      currency: currencyCode,
      currencyDisplay: "narrowSymbol",
      maximumFractionDigits: 0
    })
  const formatRemainingFunds = (amount: number) =>
    fundSubsidy.hasUnlimitedBenefit ? intl.formatMessage({ defaultMessage: "Unlimited" }) : formatFunds(amount)
  const remainingLifetime = intl.formatMessage({ defaultMessage: "Remaining lifetime" })
  const used = intl.formatMessage({ defaultMessage: "Used", description: "money spent" })
  return (
    <>
      <Typography
        variant="body1"
        sx={(theme) => ({ color: theme.palette.text.secondary, paddingBlockEnd: theme.tokens.spacing.xl })}
      >
        <FormattedMessage defaultMessage="Your benefit sponsor offers financial support to make eligible care and services more affordable." />
      </Typography>
      <BalanceSection
        title={<FormattedMessage defaultMessage="Available credits" />}
        description={
          <FormattedMessage
            defaultMessage="{cycleSentence}You can <link>use credits</link> for eligible fertility services at eligible providers."
            values={{
              link: dbgLink,
              cycleSentence: <CarrotCycles creditLifetimeMax={creditSubsidy.creditLifetimeMax} />
            }}
          />
        }
      >
        <BalanceTile title={remainingLifetime} value={formatCredits(creditSubsidy.remainingCredits)} />
        <BalanceTile
          title={used}
          value={formatCredits(creditSubsidy.creditLifetimeMax - creditSubsidy.remainingCredits)}
        />
      </BalanceSection>
      {fundSubsidy && (
        <BalanceSection
          title={<FormattedMessage defaultMessage="Additional funds" />}
          description={
            <FormattedMessage
              defaultMessage="You can <link>use funds</link> for eligible services not covered by your credits."
              values={{ link: dbgLink }}
            />
          }
        >
          {fundSubsidy.yearlyAmounts.max !== fundSubsidy.lifetimeAmounts.max && (
            <BalanceTile
              title={intl.formatMessage({ defaultMessage: "Remaining annual" })}
              value={formatRemainingFunds(fundSubsidy.yearlyAmounts.available)}
            />
          )}
          {(fundSubsidy.lifetimeAmounts.max !== null || fundSubsidy.hasUnlimitedBenefit) && (
            <BalanceTile
              title={remainingLifetime}
              value={formatRemainingFunds(fundSubsidy.lifetimeAmounts.available)}
            />
          )}
          <BalanceTile title={used} value={formatFunds(fundSubsidy.lifetimeAmounts.claimed)} />
        </BalanceSection>
      )}
    </>
  )
}

interface SingleCategoryReimbursementsListProps {
  reimbursementsList: any[]
  subsidy: any
  // CREDIT members only; subsidy is then the paired fund, or the credit plan when there is none.
  creditSubsidy?: Subsidy
  hideBalance?: boolean
  onTransactionClick?: (transaction: any) => void
}

export const SingleCategoryReimbursementsList = ({
  reimbursementsList,
  subsidy,
  creditSubsidy,
  hideBalance = false,
  onTransactionClick
}: SingleCategoryReimbursementsListProps) => {
  const intl = useIntl()
  const theme = useTheme()
  const reimbursementsRequiringAction = getReimbursementsRequiringActionList(reimbursementsList)
  const reimbursementsPending = getPendingReimbursementsList(reimbursementsList)
  const reimbursementsCompleted = getCompletedReimbursementsList(reimbursementsList)
  const textDirection = useTextDirection()
  const allSubsidies = useSelector(getSubsidies)

  return (
    <>
      {hideBalance ? null : creditSubsidy ? (
        <Box>
          <CreditBalances creditSubsidy={creditSubsidy} fundSubsidy={shouldShowPairedFunds(subsidy) ? subsidy : null} />
          <PlanInfoBadgeContainer
            subsidy={subsidy}
            allSubsidies={allSubsidies}
            sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.md })}
          />
        </Box>
      ) : subsidy.hasUnlimitedBenefit ? null : (
        <>
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xxxl })}>
            <Typography
              variant="h4"
              component="h2"
              color={theme.palette.text.primary}
              paddingBottom={(theme) => theme.tokens.spacing.md}
            >
              <FormattedMessage defaultMessage="Available funds" description="money available to be spent" />
            </Typography>
            <ReimbursementSummary
              subsidy={subsidy}
              reimbursementsCount={reimbursementsList ? reimbursementsList.length : 0}
              showRemainingAnnual={
                !subsidy.hasUnlimitedBenefit && subsidy.yearlyAmounts.max !== subsidy.lifetimeAmounts.max
              }
              showRemainingLifetime={!subsidy.hasUnlimitedBenefit && subsidy.lifetimeAmounts.max !== null}
              // @ts-expect-error TS(2322) FIXME: Type '{ subsidy: any; reimbursementsCount: any; sh... Remove this comment to see the full error message
              paddingTop={(theme) => theme.tokens.spacing.md}
              // @ts-expect-error TS(7006) FIXME: Parameter 'theme' implicitly has an 'any' type.
              paddingBottom={(theme) => theme.tokens.spacing.md}
            />
            <PlanInfoBadgeContainer
              subsidy={subsidy}
              // @ts-expect-error TS(2322) FIXME: Type '{ subsidy: any; categorySupportsDeductibles:... Remove this comment to see the full error message
              categorySupportsDeductibles={true}
              sx={(theme) => ({ paddingBlockStart: theme.tokens.spacing.md })}
            />
          </Box>
        </>
      )}
      {reimbursementsList?.length > 0 ? (
        <>
          <Box sx={(theme) => ({ marginBlockStart: theme.tokens.spacing.xs })}>
            <Typography variant="h6" component="h2" sx={{ fontSize: "20px" }}>
              <FormattedMessage defaultMessage="Activity" description="things that have been done" />
            </Typography>
          </Box>

          <ReimbursementsListSection
            title={
              <Stack
                direction={textDirection === TextDirection.RightToLeft ? "row-reverse" : "row"}
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={(theme) => theme.tokens.spacing.xl}
              >
                <Badge
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: textDirection === TextDirection.RightToLeft ? "left" : "right"
                  }}
                  badgeContent={intl.formatNumber(reimbursementsRequiringAction.length)}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge.MuiBadge-anchorOriginTopRight": {
                      insetBlockStart: "50%",
                      transform: "scale(1) translate(26px, -50%)"
                    },
                    "& .MuiBadge-badge.MuiBadge-anchorOriginTopLeft": {
                      insetBlockStart: "50%",
                      transform: "scale(1) translate(-26px, -50%)"
                    }
                  }}
                >
                  <Typography
                    variant="h3"
                    color={(theme) => theme.palette.text.secondary}
                    sx={{ ...theme.typography.chip }}
                  >
                    <FormattedMessage defaultMessage="Needs Attention" />
                  </Typography>
                </Badge>
                <Legend />
              </Stack>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No transactions need attention" })}
            items={reimbursementsRequiringAction}
            showAlert={reimbursementsRequiringAction.some(
              (e) =>
                e.expenseState === ExpenseState.NEEDS_MEMBER_UPLOADS ||
                e.expenseState === ExpenseState.DENIED_RECONCILIATION_REQUESTED ||
                e.reconciliationRequestedExpenseId
            )}
            onTransactionClick={onTransactionClick}
          />
          <ReimbursementsListSection
            title={
              <Typography
                variant="h3"
                color={(theme) => theme.palette.text.secondary}
                sx={{ ...theme.typography.chip }}
              >
                <FormattedMessage defaultMessage="Pending" />
              </Typography>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No pending transactions" })}
            items={reimbursementsPending}
            onTransactionClick={onTransactionClick}
          />
          <ReimbursementsListSection
            title={
              <Typography
                variant="h3"
                color={(theme) => theme.palette.text.secondary}
                sx={{ ...theme.typography.chip }}
              >
                <FormattedMessage defaultMessage="Complete" />
              </Typography>
            }
            emptyText={intl.formatMessage({ defaultMessage: "No completed transactions" })}
            items={reimbursementsCompleted}
            onTransactionClick={onTransactionClick}
          />
        </>
      ) : (
        <EmptyReimbursementsList />
      )}
    </>
  )
}
